import { useEffect, useRef, useState } from "react";
import {
  Typography,
  Grid,
  Button,
  Dialog,
  DialogActions,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  DialogContentText,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  ArrowLeftOutlined,
  ChevronRightOutlined,
  DeleteOutline,
  EditOutlined,
  ForkRightOutlined,
  ListOutlined,
  UploadFileOutlined,
  UploadOutlined,
} from "@mui/icons-material";
import { getItems } from "../../utils/api/items/getItems";
import { createItem } from "../../utils/api/items/createItem";
import { updateItem } from "../../utils/api/items/updateItem";
import { destroyItem } from "../../utils/api/items/destroyItem";
import { useFileUploadMutation } from "../../utils/api/items/useFileUploadMutation";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router";
import { useSnackbar } from "notistack";
import itemPhoto from "../../styles/images/photo-placeholder.jpg";
import itemThumbnail from "../../styles/images/thumbnail-placeholder.png";
import styles from "./items.module.css";

const PHOTOS_ENDPOINT = process.env.REACT_APP_PHOTOS_ENDPOINT;

const Items = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const photoInputRef = useRef(null);
  const [dialog, setDialog] = useState(null);
  const [destroyDialog, setDestroyDialog] = useState(null);
  const [uploadType, setUploadType] = useState(null);
  const params = useParams();
  const navigate = useNavigate();

  const handleDialogClose = () => setDialog(null);
  const handleDestroyDialogClose = () => setDestroyDialog(null);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const {
    mutation: uploadFileMutation,
    progress: uploadFileProgress,
    result: uploadFileResult,
  } = useFileUploadMutation();

  const onCreateSubmit = (data) => createItemMutation.mutate({ category_id: params.categoryId, ...data });
  const onUpdateSubmit = (data) => updateItemMutation.mutate({ id: dialog.id, ...data });

  const handleDialogOpen = (data) => {
    setValue("title", data.title);
    setValue("description", data.description);
    setValue("price", data.price);
    setDialog(data);
  };

  useEffect(() => {
    getItemsQuery.refetch();
    closeSnackbar();
  }, [uploadFileResult]);

  const handleUploadPhoto = (e) => {
    uploadFileMutation.mutate({
      id: uploadType.id,
      file: e.target.files[0],
      rowId: uploadType.id,
      fileType: uploadType.type,
      rowType: "item",
    });
    enqueueSnackbar("در حال آپلود" + uploadFileProgress, {
      persist: true,
    });
  };

  const getItemsQuery = useQuery(["get-items", params], () => getItems({ id: params.categoryId }), {
    onSuccess: (data) => {},
    onError: (error) => {
      enqueueSnackbar("خطا در دریافت آیتم ها", { variant: "error" });
    },
  });

  const createItemMutation = useMutation("create-item-mutation", (data) => createItem(data), {
    onSuccess: (data) => {
      enqueueSnackbar("آیتم با موفقیت ایجاد شد", { variant: "success" });
      handleDialogClose();
      getItemsQuery.refetch();
    },
    onError: (error) => {
      enqueueSnackbar("خطا در ایجاد آیتم", { variant: "error" });
    },
  });

  const updateItemMutation = useMutation("update-item-mutation", (data) => updateItem(data), {
    onSuccess: (data) => {
      enqueueSnackbar("آیتم با موفقیت ویرایش شد", { variant: "success" });
      handleDialogClose();
      getItemsQuery.refetch();
    },
    onError: (error) => {
      enqueueSnackbar("خطا در ویرایش آیتم", { variant: "error" });
    },
  });

  const destroyItemMutation = useMutation("destroy-item-mutation", (data) => destroyItem(data), {
    onSuccess: (data) => {
      enqueueSnackbar("آیتم با موفقیت حذف شد", { variant: "success" });
      handleDestroyDialogClose();
      getItemsQuery.refetch();
    },
    onError: (error) => {
      enqueueSnackbar("خطا در حذف آیتم", { variant: "error" });
    },
  });

  return (
    <>
      <Grid container>
        <Grid item size={12}>
          <Grid container direction={"row"} flexWrap={"nowrap"} alignItems={"center"} justifyContent={"center"}>
            <Tooltip title='بازگشت' arrow style={{ marginLeft: 8 }}>
              <IconButton size='small' color='secondary' onClick={() => navigate("/panel/categories")}>
                <ChevronRightOutlined />
              </IconButton>
            </Tooltip>
            <Typography>آیتم‌ها</Typography>
            <div className={styles["fg-1"]} />
            <Button
              variant='contained'
              color='primary'
              onClick={() =>
                handleDialogOpen({
                  type: "create",
                  id: null,
                  title: "",
                  description: "",
                  price: "",
                })
              }
            >
              افزودن آیتم
            </Button>
          </Grid>
        </Grid>

        <Grid item size={12}>
          <Grid container marginTop={2} alignItems={"flex-start"} justifyContent={"flex-start"} spacing={2}>
            {getItemsQuery?.isLoading ? (
              <Typography>در حال بارگذاری...</Typography>
            ) : getItemsQuery?.isError ? (
              <Typography>خطا در بارگذاری آیتم ها</Typography>
            ) : getItemsQuery?.data?.data?.items?.length === 0 ? (
              <Typography>آیتمی وجود ندارد</Typography>
            ) : (
              getItemsQuery?.data?.data?.items?.map((item) => (
                <Grid item key={item.id} size={{ xs: 12, sm: 4, md: 3 }}>
                  <Paper className={styles["item-paper"]}>
                    <Grid container alignItems={"flex-start"} alignContent={"center"} direction={"column"} spacing={1}>
                      <Grid item size={12} direction={"column"} position={"relative"}>
                        <img
                          src={item.photo_url ? `${PHOTOS_ENDPOINT}/${item.photo_url}` : itemPhoto}
                          alt={item.title}
                          className={styles["item-photo"]}
                        />
                        <Typography variant='caption'>نسبت تصویر پیشنهادی: ۱۴:۹</Typography>

                        {/* <img
                          src={item.thumbnail_url ? `${PHOTOS_ENDPOINT}/${item.thumbnail_url}` : itemThumbnail}
                          alt={item.title}
                          className={styles["item-thumbnail"]}
                        /> */}
                        <Typography variant='h6' style={{ marginTop: 10 }}>
                          {item.title}
                        </Typography>
                        <Typography variant='body2'>{item.description}</Typography>
                        <Typography variant='body2'>{item.price}</Typography>
                      </Grid>
                      <Grid item size={12}>
                        <Grid container direction={"row"} spacing={1}>
                          <Tooltip title='ویرایش آیتم' arrow>
                            <IconButton
                              size='medium'
                              color='secondary'
                              onClick={() =>
                                handleDialogOpen({
                                  type: "update",
                                  id: item.id,
                                  title: item.title,
                                  description: item.description,
                                  price: item.price,
                                })
                              }
                            >
                              <EditOutlined />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='حذف آیتم' arrow>
                            <IconButton
                              size='medium'
                              color='secondary'
                              onClick={() => setDestroyDialog({ id: item.id })}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='آپلود تصویر آیتم' arrow>
                            <IconButton
                              size='medium'
                              color='secondary'
                              onClick={() => {
                                photoInputRef.current.value = null;
                                photoInputRef.current.click();
                                setUploadType({ id: item.id, type: "photo" });
                              }}
                              disabled={uploadFileMutation.isLoading}
                            >
                              <UploadFileOutlined />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title='آپلود آیکون آیتم' arrow>
                            <IconButton
                              size='medium'
                              color='secondary'
                              onClick={() => {
                                photoInputRef.current.value = null;
                                photoInputRef.current.click();
                                setUploadType({ id: item.id, type: "thumbnail" });
                              }}
                            >
                              <UploadOutlined />
                            </IconButton>
                          </Tooltip> */}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))
            )}
          </Grid>
        </Grid>
      </Grid>

      <input
        type='file'
        id='photo_upload'
        accept='image/*'
        onChange={handleUploadPhoto}
        ref={photoInputRef}
        style={{ display: "none" }}
      />

      <Dialog open={destroyDialog} onClose={handleDestroyDialogClose}>
        <DialogTitle>حذف آیتم</DialogTitle>
        <DialogContent>
          <DialogContentText>آیا مایل به حذف این آیتم هستید؟ این عملیات غیرقابل بازگشت است.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDestroyDialogClose}>لغو</Button>

          <Button
            variant='contained'
            color='error'
            onClick={() => destroyItemMutation.mutate({ id: destroyDialog?.id })}
            loading={destroyItemMutation.isLoading}
            disabled={destroyItemMutation.isLoading}
          >
            حذف آیتم
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog} onClose={handleDialogClose}>
        <DialogTitle>{dialog?.type === "update" ? "ویرایش" : "ایجاد"} آیتم</DialogTitle>
        <DialogContent>
          {/* <DialogContentText>ایجاد آیتم</DialogContentText> */}
          <form onSubmit={handleSubmit(dialog?.type === "update" ? onUpdateSubmit : onCreateSubmit)}>
            <Controller
              name='title'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.title}
                  helperText={errors.title && "نام آیتم الزامی است."}
                  className={styles.textFields}
                  disabled={createItemMutation.isLoading || updateItemMutation?.isLoading}
                  variant='outlined'
                  label='نام آیتم'
                  margin='dense'
                  fullWidth
                />
              )}
            />

            <Controller
              name='description'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.description}
                  helperText={errors.description && "توضیحات الزامی است."}
                  className={styles.textFields}
                  disabled={createItemMutation.isLoading || updateItemMutation?.isLoading}
                  variant='outlined'
                  label='توضیحات'
                  margin='dense'
                  fullWidth
                />
              )}
            />
            <Controller
              name='price'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  error={errors.price}
                  helperText={errors.price && "قیمت الزامی است."}
                  className={styles.textFields}
                  disabled={createItemMutation.isLoading || updateItemMutation?.isLoading}
                  variant='outlined'
                  label='قیمت'
                  margin='dense'
                  fullWidth
                />
              )}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>لغو</Button>

          <Button
            variant='contained'
            onClick={handleSubmit(dialog?.type === "update" ? onUpdateSubmit : onCreateSubmit)}
            loading={createItemMutation.isLoading || updateItemMutation?.isLoading}
            disabled={createItemMutation.isLoading || updateItemMutation?.isLoading}
          >
            {dialog?.type === "update" ? "ویرایش" : "ایجاد"} آیتم
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Items;
